import React, { ComponentType, useEffect, useRef, useState } from 'react'
import { CheckoutPage } from '../../model'
import { useIsDesignLanguage, DesignLanguage, Loader } from '@afterpaytouch/core'
import { ArcadeSummaryPage } from '../summary-arcade'
import { BronteSummaryPage } from '../summary-bronte'
import { SummaryNotificationProvider } from '../../component'
import { useIsAppleRetailOrder } from '../../state/checkout/hooks'
import { AppleRetailSummaryPage } from '../summary-apple-retail'
import { useIsConsumerLendingOrder } from '../../state/consumerLending/hooks'
import { useConsumerFeatureFlagReady } from '../../state/consumer/hooks'
import { PayMonthlySummaryRedesignedPage } from '../summary-pay-monthly-redesign'
import { useAutoRememberMeIssuanceToast } from '../../component/AutoRememberMeIssuanceToast'

// How long the PM summary waits for LaunchDarkly before pinning off unresolved flag reads. LD
// readies well under a second normally; the timeout only exists so an LD outage (or an identify
// that never completes) can't strand the consumer on a loader in the purchase path.
const FLAG_PIN_TIMEOUT_MS = 3000

// The routing outcomes a consumer-lending (PCL) order can pin to. The decision is pinned because
// `useIsAppleRetailOrder` is flag-gated, so it is exposed to LD readiness/re-identify churn.
type PayMonthlySummaryRoute = 'appleRetail' | 'payMonthly'

export const SummaryPage: CheckoutPage = () => {
  const isArcadeDesignLanguage = useIsDesignLanguage(DesignLanguage.Arcade)
  const isAppleRetailOrder = useIsAppleRetailOrder()
  const isConsumerLendingOrder = useIsConsumerLendingOrder()
  const hasFeatureFlagsReady = useConsumerFeatureFlagReady()
  const [flagPinTimedOut, setFlagPinTimedOut] = useState(false)

  // CASHDAP-3090: pin the PM summary ROUTE once per mount. Flag reads return their defaults until
  // LD readies, and every LD re-identify clears the flag cache mid-page — routing off live values
  // could bounce an Apple Retail PCL order onto the Pay Monthly page (full remount when
  // `useIsAppleRetailOrder`'s flag settled), so no flag movement can reroute a mounted summary.
  const pinnedRouteRef = useRef<PayMonthlySummaryRoute | null>(null)
  if (pinnedRouteRef.current === null && isConsumerLendingOrder && (hasFeatureFlagsReady || flagPinTimedOut)) {
    pinnedRouteRef.current = isAppleRetailOrder ? 'appleRetail' : 'payMonthly'
  }
  const pinnedRoute = pinnedRouteRef.current

  useEffect(() => {
    const timer = window.setTimeout(() => setFlagPinTimedOut(true), FLAG_PIN_TIMEOUT_MS)
    return () => window.clearTimeout(timer)
  }, [])

  // CASHDAP-3292: first-time remember-me issuance toast — mounted on the variant switcher so every
  // summary variant is covered. Self-gated on the one-shot mint signal (only set when a remember-me
  // session was actually minted this visit), so it's a no-op for everyone else.
  useAutoRememberMeIssuanceToast()

  // Typed ComponentType (rendered bare below, no page statics used): the pages mix `CheckoutPage`
  // and prop-taking `FunctionComponent` declarations, whose direct cross-assignment fails the
  // next-build typecheck.
  let SummaryComponent: ComponentType = BronteSummaryPage

  if (isConsumerLendingOrder) {
    if (pinnedRoute === null) {
      // Waiting on the first LD resolution (bounded by FLAG_PIN_TIMEOUT_MS) — a brief loader beats
      // mounting a page and swapping it out from under the consumer. For an Apple Retail PCL order
      // this covers the window where `useIsAppleRetailOrder` (flag-gated) still reads false: the
      // pin then routes it straight to the Apple Retail summary without ever mounting the PM page.
      return <Loader alignCenter withHeader />
    }
    SummaryComponent = pinnedRoute === 'appleRetail' ? AppleRetailSummaryPage : PayMonthlySummaryRedesignedPage
  } else if (isAppleRetailOrder) {
    // Non-consumer-lending Apple Retail order: no route ambiguity, route live as before.
    SummaryComponent = AppleRetailSummaryPage
  } else if (isArcadeDesignLanguage) {
    SummaryComponent = ArcadeSummaryPage
  }

  // TODO: Fix rerender flashing between diff summary component causing Arcade Summary toast to show up during manual page refresh, useIsAppleRetailOrder flag value should cache

  return (
    <SummaryNotificationProvider>
      <SummaryComponent />
    </SummaryNotificationProvider>
  )
}
